/**
 * Boolean query parser supporting AND, OR, NOT, parentheses
 * Grammar:
 *   expr   := term (OR term)*
 *   term   := factor (AND factor)*
 *   factor := NOT factor | '(' expr ')' | atom
 *   atom   := quoted_string | word
 */

const TOKEN_TYPES = {
    AND: "AND",
    OR: "OR",
    NOT: "NOT",
    LPAREN: "LPAREN",
    RPAREN: "RPAREN",
    ATOM: "ATOM",
    EOF: "EOF",
}

function tokenize(input) {
    const tokens = []
    let i = 0
    const str = input.trim()

    while (i < str.length) {
        // skip whitespace
        if (/\s/.test(str[i])) {
            i++
            continue
        }

        if (str[i] === "(") {
            tokens.push({ type: TOKEN_TYPES.LPAREN })
            i++
            continue
        }

        if (str[i] === ")") {
            tokens.push({ type: TOKEN_TYPES.RPAREN })
            i++
            continue
        }

        // quoted string
        if (str[i] === '"' || str[i] === "'") {
            const quote = str[i]
            i++
            let value = ""
            while (i < str.length && str[i] !== quote) {
                value += str[i]
                i++
            }
            i++ // closing quote
            tokens.push({ type: TOKEN_TYPES.ATOM, value })
            continue
        }

        // word
        let word = ""
        while (i < str.length && !/[\s()]/.test(str[i])) {
            word += str[i]
            i++
        }

        const upper = word.toUpperCase()
        if (upper === "AND") {
            tokens.push({ type: TOKEN_TYPES.AND })
        } else if (upper === "OR") {
            tokens.push({ type: TOKEN_TYPES.OR })
        } else if (upper === "NOT") {
            tokens.push({ type: TOKEN_TYPES.NOT })
        } else {
            tokens.push({ type: TOKEN_TYPES.ATOM, value: word })
        }
    }

    tokens.push({ type: TOKEN_TYPES.EOF })
    return tokens
}

function createParser(tokens) {
    let pos = 0

    function peek() {
        return tokens[pos]
    }

    function consume(type) {
        const token = tokens[pos]
        if (type && token.type !== type) {
            throw new Error(`Expected ${type} but got ${token.type}`)
        }
        pos++
        return token
    }

    function parseExpr() {
        let left = parseTerm()
        while (peek().type === TOKEN_TYPES.OR) {
            consume(TOKEN_TYPES.OR)
            const right = parseTerm()
            left = { type: "OR", left, right }
        }
        return left
    }

    function startsFactor(type) {
        return (
            type === TOKEN_TYPES.ATOM ||
            type === TOKEN_TYPES.LPAREN ||
            type === TOKEN_TYPES.NOT
        )
    }

    function parseTerm() {
        let left = parseFactor()
        while (
            peek().type === TOKEN_TYPES.AND ||
            (left !== null && startsFactor(peek().type)) // implicit AND
            ) {
            if (peek().type === TOKEN_TYPES.AND) {
                consume(TOKEN_TYPES.AND)
            }
            if (!startsFactor(peek().type)) {
                break
            }
            const right = parseFactor()
            left = { type: "AND", left, right }
        }
        return left
    }

    function parseFactor() {
        if (peek().type === TOKEN_TYPES.NOT) {
            consume(TOKEN_TYPES.NOT)
            const operand = parseFactor()
            return { type: "NOT", operand }
        }

        if (peek().type === TOKEN_TYPES.LPAREN) {
            consume(TOKEN_TYPES.LPAREN)
            const expr = parseExpr()
            consume(TOKEN_TYPES.RPAREN)
            return expr
        }

        if (peek().type === TOKEN_TYPES.ATOM) {
            const token = consume(TOKEN_TYPES.ATOM)
            return { type: "ATOM", value: token.value }
        }

        return null
    }

    function parse() {
        const ast = parseExpr()
        consume(TOKEN_TYPES.EOF)
        return ast
    }

    return { parse }
}

function parseQuery(input) {
    if (!input || !input.trim()) return null
    try {
        const tokens = tokenize(input)
        const parser = createParser(tokens)
        return parser.parse()
    } catch {
        // fallback: treat entire input as a single atom
        return { type: "ATOM", value: input.trim() }
    }
}

function evaluateAst(ast, text) {
    if (!ast) return true
    const lower = text.toLowerCase()

    switch (ast.type) {
        case "ATOM":
            return lower.includes(ast.value.toLowerCase())
        case "AND":
            return evaluateAst(ast.left, text) && evaluateAst(ast.right, text)
        case "OR":
            return evaluateAst(ast.left, text) || evaluateAst(ast.right, text)
        case "NOT":
            return !evaluateAst(ast.operand, text)
        default:
            return true
    }
}

function rowToSearchString(row) {
    try {
        return JSON.stringify(row)
    } catch {
        return Object.values(row).filter((v) => v != null).join(" ")
    }
}

export function filterRowsByQuery(rows, query) {
    if (!query || !query.trim()) return rows
    const ast = parseQuery(query)
    if (!ast) return rows

    return rows.filter((row) => {
        const searchStr = rowToSearchString(row)
        return evaluateAst(ast, searchStr)
    })
}
